/**
 * Adaptador da API PHP (/api) com a mesma superfície do supabase-js que este
 * projeto usa — e só ela.
 *
 * Por que adaptador em vez de reescrever as telas: as 13 telas e hooks que leem
 * dados usam sempre o mesmo punhado de métodos (`from().select().eq().order()`,
 * `insert`, `update().eq()`, `delete().eq()`). Traduzir isso num único lugar
 * mantém a migração num arquivo, em vez de espalhar risco por 13.
 *
 * Não é uma reimplementação do PostgREST: o que não estiver aqui lança erro,
 * para falhar alto em vez de devolver dado errado em silêncio.
 *
 * O filtro por blog é ignorado de propósito — quem escopa é o servidor, pelo
 * blog_id do config.php. `.eq('blog_id', …)` vira no-op.
 */

const API = '/api';

type Resultado<T = any> = { data: T | null; error: { message: string } | null };

const ENDPOINTS: Record<string, { url: string; type?: string }> = {
  posts: { url: `${API}/posts.php` },
  video_carousel: { url: `${API}/collections.php`, type: 'videos' },
  media_mentions: { url: `${API}/collections.php`, type: 'media' },
  gallery_cases: { url: `${API}/collections.php`, type: 'gallery' },
};

async function pedir(url: string, init?: RequestInit): Promise<Resultado> {
  try {
    const res = await fetch(url, { credentials: 'same-origin', ...init });
    const corpo = res.status === 204 ? null : await res.json().catch(() => null);
    if (!res.ok) {
      return { data: null, error: { message: corpo?.error ?? `HTTP ${res.status}` } };
    }
    return { data: corpo, error: null };
  } catch (err) {
    return { data: null, error: { message: (err as Error).message } };
  }
}

class Consulta implements PromiseLike<Resultado> {
  private filtros: Record<string, unknown> = {};
  private exclusoes: Record<string, unknown> = {};
  private ordenacoes: Array<{ coluna: string; asc: boolean }> = [];
  private limite: number | null = null;
  private umSo = false;
  private operacao: 'select' | 'insert' | 'update' | 'delete' = 'select';
  private corpo: Record<string, unknown> | null = null;

  constructor(private tabela: string) {
    if (!ENDPOINTS[tabela]) {
      throw new Error(`Tabela sem endpoint na API: ${tabela}`);
    }
  }

  // ─── Encadeamento ──────────────────────────────────────────────────────────
  select(_colunas?: string) {
    // A API sempre devolve a linha inteira; a projeção de colunas do
    // supabase-js não tem equivalente e não faz falta neste volume de dados.
    if (this.operacao === 'select') this.operacao = 'select';
    return this;
  }

  insert(valores: Record<string, unknown>) {
    this.operacao = 'insert';
    this.corpo = valores;
    return this;
  }

  update(valores: Record<string, unknown>) {
    this.operacao = 'update';
    this.corpo = valores;
    return this;
  }

  delete() {
    this.operacao = 'delete';
    return this;
  }

  eq(coluna: string, valor: unknown) {
    if (coluna !== 'blog_id') this.filtros[coluna] = valor;
    return this;
  }

  neq(coluna: string, valor: unknown) {
    this.exclusoes[coluna] = valor;
    return this;
  }

  in(coluna: string, valores: unknown[]) {
    this.filtros[coluna] = valores.join(',');
    return this;
  }

  order(coluna: string, opcoes?: { ascending?: boolean; nullsFirst?: boolean }) {
    // nullsFirst é aceito e ignorado: a ordenação local trata null como ''.
    this.ordenacoes.push({ coluna, asc: opcoes?.ascending !== false });
    return this;
  }

  limit(n: number) {
    this.limite = n;
    return this;
  }

  range(de: number, ate: number) {
    this.limite = ate - de + 1;
    return this;
  }

  maybeSingle() {
    this.umSo = true;
    return this;
  }

  single() {
    this.umSo = true;
    return this;
  }

  // ─── Execução ──────────────────────────────────────────────────────────────
  private montarUrl(): string {
    const { url, type } = ENDPOINTS[this.tabela];
    const params = new URLSearchParams();
    if (type) params.set('type', type);

    if (this.filtros.id !== undefined) params.set('id', String(this.filtros.id));
    if (this.filtros.slug !== undefined) params.set('slug', String(this.filtros.slug));
    // Painel pede tudo, inclusive rascunho e itens desativados; o servidor só
    // atende `all` se houver sessão de admin.
    if (this.operacao === 'select' && this.filtros.status === undefined && this.filtros.is_active === undefined) {
      params.set('all', '1');
    }

    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
  }

  private aplicarLocal(linhas: any[]): any[] {
    let saida = linhas;

    for (const [coluna, valor] of Object.entries(this.filtros)) {
      if (coluna === 'id' || coluna === 'slug') continue; // já filtrados no servidor
      saida = saida.filter((l) => {
        if (typeof valor === 'boolean') return Boolean(l[coluna]) === valor;
        return String(l[coluna]) === String(valor);
      });
    }

    for (const { coluna, asc } of [...this.ordenacoes].reverse()) {
      saida = [...saida].sort((a, b) => {
        const x = a[coluna] ?? '';
        const y = b[coluna] ?? '';
        if (x === y) return 0;
        return (x > y ? 1 : -1) * (asc ? 1 : -1);
      });
    }

    for (const [coluna, valor] of Object.entries(this.exclusoes)) {
      saida = saida.filter((l) => String(l[coluna]) !== String(valor));
    }

    return this.limite !== null ? saida.slice(0, this.limite) : saida;
  }

  private async executar(): Promise<Resultado> {
    const { url, type } = ENDPOINTS[this.tabela];
    const base = type ? `${url}?type=${type}` : url;
    const id = this.filtros.id;

    if (this.operacao === 'insert') {
      return pedir(base, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.corpo ?? {}),
      });
    }

    if (this.operacao === 'update') {
      if (id === undefined) return { data: null, error: { message: 'update sem id' } };
      return pedir(`${base}${type ? '&' : '?'}id=${encodeURIComponent(String(id))}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.corpo ?? {}),
      });
    }

    if (this.operacao === 'delete') {
      if (id === undefined) return { data: null, error: { message: 'delete sem id' } };
      return pedir(`${base}${type ? '&' : '?'}id=${encodeURIComponent(String(id))}`, { method: 'DELETE' });
    }

    const res = await pedir(this.montarUrl());
    if (res.error) return res;

    const linhas = Array.isArray(res.data) ? this.aplicarLocal(res.data) : res.data;
    if (this.umSo) {
      return { data: Array.isArray(linhas) ? linhas[0] ?? null : linhas, error: null };
    }
    return { data: linhas, error: null };
  }

  then<R1 = Resultado, R2 = never>(
    aoResolver?: ((v: Resultado) => R1 | PromiseLike<R1>) | null,
    aoRejeitar?: ((r: any) => R2 | PromiseLike<R2>) | null
  ): PromiseLike<R1 | R2> {
    return this.executar().then(aoResolver, aoRejeitar);
  }
}

// ─── Storage → /api/upload.php ────────────────────────────────────────────────
// O servidor gera o nome do arquivo (não aceita o do cliente), então guardamos
// o de-para entre o caminho pedido e a URL devolvida — é o que o getPublicUrl
// logo em seguida precisa. Um Map, e não uma variável, para dois uploads
// simultâneos não trocarem de URL entre si.
const urlsEnviadas = new Map<string, string>();

const storage = {
  from(_bucket: string) {
    return {
      async upload(
        caminho: string,
        arquivo: File | Blob,
        _opcoes?: { cacheControl?: string; upsert?: boolean; contentType?: string }
      ) {
        // cacheControl e contentType são decididos no servidor; upsert não se
        // aplica, porque cada upload ganha nome novo.
        const form = new FormData();
        form.append('file', arquivo, caminho.split('/').pop() || 'upload');
        const res = await pedir(`${API}/upload.php`, { method: 'POST', body: form });
        if (!res.error && res.data?.url) urlsEnviadas.set(caminho, res.data.url);
        return { data: res.error ? null : { path: res.data?.url ?? caminho }, error: res.error };
      },
      getPublicUrl(caminho: string) {
        return { data: { publicUrl: urlsEnviadas.get(caminho) ?? caminho } };
      },
    };
  },
};

// ─── Autenticação → /api/auth.php ─────────────────────────────────────────────
type Ouvinte = (evento: string, sessao: { user: any } | null) => void;
const ouvintes = new Set<Ouvinte>();

/**
 * Marcador de "havia sessão na última vez", usado só para o painel decidir
 * entre mostrar o formulário de login ou um skeleton no primeiro paint.
 * O cookie de sessão é httponly e o JavaScript não o enxerga — daí precisar
 * desta pista. Não é credencial: quem forjar isso vê um skeleton e nada mais,
 * porque toda leitura de dado passa pelo servidor.
 */
export const CHAVE_SESSAO = 'bg-admin-sessao';

function marcarSessao(existe: boolean) {
  if (typeof window === 'undefined') return;
  try {
    if (existe) window.localStorage.setItem(CHAVE_SESSAO, '1');
    else window.localStorage.removeItem(CHAVE_SESSAO);
  } catch {
    /* modo privado / storage bloqueado: só perde a otimização */
  }
}

function avisar(sessao: { user: any } | null) {
  ouvintes.forEach((fn) => fn(sessao ? 'SIGNED_IN' : 'SIGNED_OUT', sessao));
}

const auth = {
  async signInWithPassword({ email, password }: { email: string; password: string }) {
    const res = await pedir(`${API}/auth.php?action=login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (res.error) return { data: { user: null, session: null }, error: res.error };
    const user = res.data?.admin ?? null;
    marcarSessao(!!user);
    avisar(user ? { user } : null);
    return { data: { user, session: { user } }, error: null };
  },

  async signOut() {
    const res = await pedir(`${API}/auth.php?action=logout`, { method: 'POST' });
    marcarSessao(false);
    avisar(null);
    return { error: res.error };
  },

  async getSession() {
    const res = await pedir(`${API}/auth.php?action=me`);
    const user = res.data?.admin ?? null;
    marcarSessao(!!user);
    return { data: { session: user ? { user } : null }, error: res.error };
  },

  onAuthStateChange(callback: Ouvinte) {
    ouvintes.add(callback);
    // Estado inicial, para o painel não ficar preso em "carregando".
    void auth.getSession().then(({ data }) => callback(data.session ? 'INITIAL_SESSION' : 'SIGNED_OUT', data.session));
    return { data: { subscription: { unsubscribe: () => { ouvintes.delete(callback); } } } };
  },
};

/**
 * Realtime do Supabase não tem equivalente na API PHP.
 * Onde era usado (carrossel de vídeos da home), servia só para recarregar a
 * lista enquanto alguém editava no painel — o visitante vê o que carregou ao
 * abrir a página, o que é suficiente. Estes no-ops mantêm o código chamador
 * intacto; a assinatura é a mesma, e nada nunca dispara.
 */
const canalInerte = {
  on(_evento?: unknown, _filtro?: unknown, _callback?: unknown) {
    return canalInerte;
  },
  subscribe() {
    return canalInerte;
  },
};

export const bgApi = {
  from: (tabela: string) => new Consulta(tabela),
  storage,
  auth,
  channel: (_nome: string) => canalInerte,
  removeChannel: (_canal: unknown) => {},
};
